import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { CITIES, findNearestCity, getIpLocation } from './useNearestCity';

// 🌍 helper: log a visit (IP list stored as flat text in Firestore)
async function logVisit(
  key: string,
  geo: Awaited<ReturnType<typeof getIpLocation>>,
  nearest: ReturnType<typeof findNearestCity>
) {
  if (!geo) return;

  const NAMESPACE = import.meta.env.DEV ? 'nhenews-local-5173' : 'nhenews.com';
  const docRef = doc(db, 'visitCounters', `${NAMESPACE}_${key}`);

  try {
    const snap = await getDoc(docRef);
    const newLine = [
      new Date().toISOString(),
      geo.ip ?? 'unknown',
      nearest?.country ?? 'unknown',
      nearest?.state ?? 'unknown',
      nearest?.city ?? 'unknown'
    ].join(', ');

    // Get existing IP list
    const existing = snap.exists() ? snap.data().ipList ?? '' : '';

    // Update the ipList field with new line appended
    await updateDoc(docRef, {
      ipList: existing ? existing + '\n' + newLine : newLine
    });
  } catch (err) {
    console.error('logVisit error:', err);
  }
}

// 🔢 main function: hit counter and return count
export async function hitAndGetCount(key: string): Promise<number | null> {
  try {
    const NAMESPACE = import.meta.env.DEV ? 'nhenews-local-5173' : 'nhenews.com';
    const docRef = doc(db, 'visitCounters', `${NAMESPACE}_${key}`);

    // 🌍 fetch geo
    const geo = await getIpLocation();
    const nearest = findNearestCity(
      geo?.latitude ?? 0,
      geo?.longitude ?? 0,
      CITIES
    );

    const snap = await getDoc(docRef);

    if (!snap.exists()) {
      // create document with count and first ipList line
      await setDoc(docRef, { count: 1, ipList: geo ? [
        [new Date().toISOString(),
        geo.ip ?? 'unknown',
        nearest?.country ?? 'unknown',
        nearest?.state ?? 'unknown',
        nearest?.city ?? 'unknown'].join(', ')
      ].join('\n') : '' });
      return 1;
    }

    // increment count
    await updateDoc(docRef, { count: increment(1) });

    // append visit IP
    await logVisit(key, geo, nearest);

    const updatedSnap = await getDoc(docRef);
    return updatedSnap.exists() ? updatedSnap.data().count ?? 0 : 0;

  } catch (err) {
    console.error('hitAndGetCount error:', err);
    return null;
  }
}

export async function getCount(key: string): Promise<number | null> {
  try {
    const NAMESPACE = import.meta.env.DEV ? 'nhenews-local-5173' : 'nhenews.com';
    const docRef = doc(db, 'visitCounters', `${NAMESPACE}_${key}`);
    const snap = await getDoc(docRef);
    return snap.exists() ? snap.data().count ?? 0 : 0;
  } catch (err) {
    console.error('getCount error:', err);
    return null;
  }
}